"use client";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  type ConfirmRequest,
  confirm,
  registerConfirmListener,
} from "@/shared/lib/confirm";
import { cn } from "@/shared/lib/utils";
import { AlertTriangleIcon, CircleHelpIcon, LogInIcon } from "lucide-react";
import * as React from "react";

type ConfirmTone = "default" | "destructive" | "info";

const toneVisuals = {
  default: {
    icon: CircleHelpIcon,
    mediaClassName: "bg-primary/10 text-primary",
  },
  destructive: {
    icon: AlertTriangleIcon,
    mediaClassName: "bg-destructive/10 text-destructive",
  },
  info: {
    icon: LogInIcon,
    mediaClassName: "bg-primary/10 text-primary",
  },
} as const;

function resolveTone(request: ConfirmRequest | null): ConfirmTone {
  if (request?.ActionProps?.variant === "destructive") return "destructive";
  return "default";
}

function resolveIcon(tone: ConfirmTone, actionText?: React.ReactNode) {
  const text =
    typeof actionText === "string" ? actionText : String(actionText ?? "");

  if (tone === "destructive") return toneVisuals.destructive;
  if (
    text.includes("ເຂົ້າລະບົບ") ||
    text.toLowerCase().includes("login") ||
    text.includes("ອັບເດດ")
  ) {
    return toneVisuals.info;
  }
  if (text.includes("ລຶບ") || text.toLowerCase().includes("delete")) {
    return toneVisuals.destructive;
  }
  return toneVisuals.default;
}

export function Confirmer() {
  const [request, setRequest] = React.useState<ConfirmRequest | null>(null);
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    registerConfirmListener((req) => {
      if (req) {
        setRequest(req);
        setOpen(true);
      }
    });
    return () => registerConfirmListener(null);
  }, []);

  const finish = (value: boolean) => {
    request?.resolve(value);
    setOpen(false);
    window.setTimeout(() => setRequest(null), 200);
  };

  const tone = resolveTone(request);
  const { icon: Icon, mediaClassName } = resolveIcon(tone, request?.actionText);
  const actionVariant = request?.ActionProps?.variant ?? "default";
  const cancelVariant = request?.CancelProps?.variant ?? "outline";

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) finish(false);
      }}
    >
      <AlertDialogContent className="gap-0 overflow-hidden p-0 sm:max-w-md">
        <AlertDialogHeader className="flex flex-row items-start gap-4 p-6 pb-5 text-left">
          <span
            aria-hidden
            className={cn(
              "flex size-11 shrink-0 items-center justify-center rounded-full [&>svg]:size-5",
              mediaClassName,
            )}
          >
            <Icon />
          </span>

          <div className="flex flex-col gap-1.5 pt-0.5">
            {request?.title ? (
              <AlertDialogTitle className="text-base leading-snug">
                {request.title}
              </AlertDialogTitle>
            ) : null}

            {request?.description ? (
              <AlertDialogDescription className="text-pretty leading-relaxed">
                {request.description}
              </AlertDialogDescription>
            ) : null}
          </div>
        </AlertDialogHeader>

        <AlertDialogFooter className="gap-2 border-t bg-muted/30 px-6 py-4 sm:justify-end">
          <AlertDialogCancel
            variant={cancelVariant}
            className={cn("sm:min-w-24", request?.CancelProps?.className)}
            onClick={() => finish(false)}
          >
            {request?.cancelText ?? "ຍົກເລີກ"}
          </AlertDialogCancel>
          <AlertDialogAction
            variant={actionVariant}
            className={cn("sm:min-w-24", request?.ActionProps?.className)}
            onClick={() => finish(true)}
          >
            {request?.actionText ?? "ຕົກລົງ"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// Re-export for convenience where both are needed
export { confirm };
